import React from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../services/auth";
import { useAuth } from "../context/AuthContext";

const BANNER_CLASS =
  "bg-surface-2 text-fg px-4 py-2 flex items-center justify-between text-sm font-medium";

/**
 * Pinned-top banner shown whenever an admin is acting as another user.
 * Detection reads the `impersonated_by` claim off the cookie JWT so every
 * tab on the origin sees the same state — sessionStorage only holds the
 * admin-restore stash for the tab that started impersonation. "Stop"
 * restores from that stash, or logs out when no stash is present.
 *
 * Mount once at the App level. Renders nothing when not impersonating, so
 * it's safe to keep on every route.
 */
const ImpersonationBanner: React.FC = () => {
  const { userId, setAuthState } = useAuth();
  const navigate = useNavigate();
  const impersonating = authService.isImpersonating();

  if (!impersonating || !userId) return null;

  const handleStop = async () => {
    await authService.stopImpersonating();
    const restored = authService.getStoredAuth();
    setAuthState({
      token: restored.token,
      refreshToken: restored.refreshToken,
      userId: restored.userId,
      isAdmin: restored.isAdmin,
      adminUserId: restored.adminUserId,
      userType: restored.userType,
      agreementAccepted: restored.agreementAccepted,
    });
    navigate("/admin/users");
  };

  return (
    <div data-testid="impersonation-banner" className={BANNER_CLASS}>
      <span>
        Acting as <span className="font-mono">{userId}</span> — actions you take are tagged with
        your admin account.
      </span>
      <button
        type="button"
        onClick={handleStop}
        className="bg-black/20 hover:bg-black/30 text-fg rounded px-3 py-1 text-xs font-semibold"
      >
        Stop impersonating
      </button>
    </div>
  );
};

export default ImpersonationBanner;
